"use client";

import { NFONE_SUPPORT_EMAIL } from "@newform/constants";
import { BookOpen, ShieldCheck } from "lucide-react";
import { useRouter } from "next/navigation";
import { useTranslations } from "next-intl";
import { useState } from "react";
import { Button } from "@/components/ui/design-system/button";
import { trpc } from "@/lib/trpc-client";
import { DocsModal, type PortalDocId } from "./DocsModal";
import { HandlesPrompt, handlesNeedAttention } from "./HandlesPrompt";
import { HomeScreen } from "./one/HomeScreen";
import { HomeSkeleton } from "./one/HomeSkeleton";
import { Quiet, Secondary } from "./one/ui";
import { InvalidLinkNotice, portalAccessNotice } from "./PortalShell";
import { usePortalSettings } from "./portalSettings";
import { OpenRequestsCard } from "./requests-view/OpenRequestsCard";
import { SubmissionsPausedNotice } from "./SubmissionsPausedNotice";
import { activeSubmissionsPause } from "./submissionsPaused";
import { TopAdsRow } from "./TopAdsRow";
import { portalHref, useCreatorPortalAccess } from "./useCreatorPortalAccess";
import { needsYou } from "./videos-view/videoFilters";

export function PortalHome() {
  const t = useTranslations("home");
  const access = useCreatorPortalAccess();
  const router = useRouter();
  const [openDoc, setOpenDoc] = useState<PortalDocId | null>(null);
  const settings = usePortalSettings();

  // The same query the shell reads for its footer, so this costs nothing.
  const messaging = trpc.creatorMessaging.auth.config.useQuery();
  const supportEmail = messaging.data?.supportEmail ?? NFONE_SUPPORT_EMAIL;

  const home = trpc.creatorPortal.getPortalHome.useQuery(access ?? {}, {
    enabled: !!access,
    retry: false,
  });
  // Separate query: a cold ads cache takes tens of seconds server-side, so
  // the page shell renders immediately and this section streams in.
  const topAds = trpc.creatorPortal.getPortalTopAds.useQuery(access ?? {}, {
    enabled: !!access,
    retry: false,
    staleTime: 5 * 60 * 1000,
  });
  // The money and the needs-you count, which live with the submissions rather
  // than with the program. Streamed like the ads for the same reason: this
  // one signs a URL and looks up spend for every video the creator ever sent,
  // so blocking the page on it would trade the whole screen for two figures.
  const mine = trpc.creatorPortal.getMySubmissions.useQuery(access ?? {}, {
    enabled: !!access,
    retry: false,
  });

  if (access === null) return <InvalidLinkNotice />;
  const accessNotice = portalAccessNotice(home.error?.data?.code);
  if (accessNotice) return accessNotice;

  if (home.error) {
    return (
      <div className="flex flex-col items-center gap-nf-ds-3 py-nf-ds-24">
        <p className="font-nf-ds-sans text-[14px] text-muted-foreground">
          {t("loadFailed")}
        </p>
        <Button variant="outline" size="sm" onClick={() => home.refetch()}>
          {t("retry")}
        </Button>
      </div>
    );
  }

  if (access === undefined || home.isLoading || !home.data) {
    return <HomeSkeleton />;
  }

  const { creator, program, docs, complianceRules } = home.data;
  const hasRules = complianceRules.length > 0;
  const waiting =
    mine.data?.submissions.filter((submission) =>
      needsYou(submission, program.tiktokEnabled),
    ).length ?? 0;
  const pause = activeSubmissionsPause(program);
  const needsHandles =
    !!settings &&
    handlesNeedAttention(creator.tiktokHandle, creator.instagramHandle);

  return (
    <>
      <HomeScreen
        firstName={creator.name.split(" ")[0]}
        // Quiet first, then the one that matters — the reading order of the
        // pair is what says which is which, on top of the treatment. Neither
        // is filled: "Submit a video" is this page's one filled control, and
        // the button role's own note says a page of filled purple has no
        // hierarchy left to spend. The ring is the standout here.
        headerActions={
          <>
            {/* Header scale, not page scale: `Primary` and `Secondary` are
                sized for the one action a screen is for, and at 40 points
                these sat as heavy as "Submit a video" while being the
                quietest thing on the page. The small control height is what
                the DS `Button` already uses for a dense row. */}
            <Quiet
              href={`mailto:${supportEmail}`}
              className="lg:h-nf-ds-control-sm lg:px-nf-ds-3 lg:text-nf-ds-sm"
            >
              {t("support")}
            </Quiet>
            <Secondary
              onClick={() => router.push(portalHref("/one/inbox", access))}
              className="lg:h-nf-ds-control-sm lg:px-nf-ds-3 lg:text-nf-ds-sm"
            >
              {t("messageUs")}
            </Secondary>
          </>
        }
        // Gated here rather than by the prompt returning null: an element that
        // renders nothing still fills its slot, and the slot has a section gap
        // on both sides of it. The pause comes first, because it is why the
        // submit button below is held, and handles only matter once a video
        // can go in.
        notice={
          pause || needsHandles ? (
            <div className="flex flex-col gap-nf-ds-block">
              {pause ? (
                <SubmissionsPausedNotice brand={program.name} pause={pause} />
              ) : null}
              {needsHandles && settings ? (
                <HandlesPrompt
                  tiktokHandle={creator.tiktokHandle}
                  instagramHandle={creator.instagramHandle}
                  onOpen={settings.open}
                />
              ) : null}
            </div>
          ) : null
        }
        needsYou={waiting}
        needsYouDetail={t("needsYou.detail", { count: waiting })}
        onNeedsYou={() => router.push(portalHref("/one/videos", access))}
        // Zero until the query lands rather than a skeleton: the figures sit
        // inside a pane whose height does not change, so the card is in its
        // final place either way and only the numbers settle.
        outstandingUsd={mine.data?.totalOutstandingUsd ?? 0}
        paidUsd={mine.data?.totalPaidUsd ?? 0}
        payoutRequestMinimumUsd={program.payoutRequestMinimumUsd}
        payoutSpendSharePct={program.payoutSpendSharePct}
        onSubmit={() => router.push(portalHref("/one/submit", access))}
        submitDisabled={!!pause}
        topAds={
          <TopAdsRow
            ads={topAds.data?.topAds ?? []}
            isLoading={topAds.isLoading}
            isError={topAds.isError}
            onRetry={() => topAds.refetch()}
          />
        }
        // Only what this program actually has. An unavailable document was a
        // dimmed tile you could see and not open; as a row the honest move is
        // to leave it out, and say so in the list's own empty state.
        docs={[
          ...(hasRules
            ? [
                {
                  id: "compliance" satisfies PortalDocId,
                  // One word, because the tile is 258 points wide and
                  // "Compliance — dos and don'ts" wrapped to two lines at the
                  // card-title role while "Creative brief" sat on one — so
                  // two cards in the same row had text blocks of different
                  // heights. The dos and don'ts were the document's contents
                  // anyway; the line under the title says what they are.
                  title: t("docs.compliance.title"),
                  detail: t("docs.compliance.detail"),
                  icon: <ShieldCheck className="size-[20px]" strokeWidth={2} />,
                },
              ]
            : []),
          ...(docs.brief
            ? [
                {
                  id: "brief" satisfies PortalDocId,
                  // Paired with Compliance: both open on "What", both name
                  // what the creator gets rather than what the document
                  // contains, and both run to two lines in a 258-point tile.
                  // The old line was a colon and a list of three, which is a
                  // table of contents — and the longest detail in the row.
                  title: t("docs.brief.title"),
                  detail: t("docs.brief.detail"),
                  icon: <BookOpen className="size-[20px]" strokeWidth={2} />,
                },
              ]
            : []),
        ]}
        onOpenDoc={(id) => setOpenDoc(id as PortalDocId)}
        // The prototype opens a sheet; here it is a page of its own. It used
        // to scroll to a card further down this one, which is a control whose
        // whole job was to move the page — and below the fold a scroll reads
        // as nothing having happened.
        onCheckScript={() => router.push(portalHref("/one/script", access))}
        onMessageTeam={() => router.push(portalHref("/one/inbox", access))}
        extras={
          <OpenRequestsCard
            items={home.data.openRequests}
            count={home.data.openRequestCount}
            access={access}
          />
        }
      />

      <DocsModal
        docs={docs}
        complianceRules={complianceRules}
        openDoc={openDoc}
        onOpenChange={(open) => {
          if (!open) setOpenDoc(null);
        }}
      />
    </>
  );
}
